<template>
  <div class="screenshot-editor">
    <ModalAreYouSure
      v-model="showClearModal"
      title="Effacer tous les dessins"
      message="Êtes-vous sûr de vouloir effacer tous les dessins ? Cette action ne pourra pas être annulée."
      confirm-text="Effacer"
      cancel-text="Annuler"
      @confirm="confirmClear"
      @cancel="cancelClear"
    />
    <!-- Toolbar -->
    <div class="toolbar bg-white border-b border-gray-200 p-2 flex items-center gap-2">
      <div class="flex items-center gap-1 border-r border-gray-300 pr-2">
        <button
          @click="setTool('circle')"
          :class="[
            'p-2 rounded hover:bg-gray-100 transition-colors',
            currentTool === 'circle' ? 'bg-bdc-ocre text-white' : 'text-gray-700',
          ]"
          :title="'Dessiner un cercle'"
        >
          <Circle class="w-5 h-5" />
        </button>
        <button
          @click="setTool('arrow')"
          :class="[
            'p-2 rounded hover:bg-gray-100 transition-colors',
            currentTool === 'arrow' ? 'bg-bdc-ocre text-white' : 'text-gray-700',
          ]"
          :title="'Dessiner une flèche'"
        >
          <ArrowRight class="w-5 h-5" />
        </button>
      </div>

      <div class="flex items-center gap-1 border-r border-gray-300 pr-2">
        <button
          @click="undo"
          :disabled="!canUndo"
          class="p-2 rounded hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-gray-700"
          :title="'Annuler'"
        >
          <Undo class="w-5 h-5" />
        </button>
        <button
          @click="redo"
          :disabled="!canRedo"
          class="p-2 rounded hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-gray-700"
          :title="'Refaire'"
        >
          <Redo class="w-5 h-5" />
        </button>
      </div>

      <div class="flex items-center gap-1 border-r border-gray-300 pr-2">
        <label class="text-sm text-gray-700 flex items-center gap-2">
          <span>Couleur:</span>
          <input
            v-model="strokeColor"
            type="color"
            class="w-8 h-8 rounded border border-gray-300 cursor-pointer"
          />
        </label>
        <label class="text-sm text-gray-700 flex items-center gap-2">
          <span>Taille:</span>
          <input v-model.number="strokeWidth" type="range" min="2" max="20" class="w-20" />
          <span class="text-xs w-6">{{ strokeWidth }}px</span>
        </label>
      </div>

      <div class="flex-1"></div>

      <button
        @click="clear"
        class="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
      >
        Effacer tout
      </button>
      <button
        @click="save"
        class="px-4 py-2 text-sm font-medium text-white bg-bdc-ocre hover:bg-bdc-terracotta rounded transition-colors"
      >
        Enregistrer
      </button>
    </div>

    <!-- Canvas Container -->
    <div
      class="canvas-container bg-gray-50 flex items-center justify-center overflow-auto"
      ref="containerRef"
    >
      <canvas
        ref="canvasRef"
        @mousedown="startDrawing"
        @mousemove="draw"
        @mouseup="stopDrawing"
        @mouseleave="stopDrawing"
        class="cursor-crosshair"
      ></canvas>
    </div>
  </div>
</template>

<script setup lang="ts">
import ModalAreYouSure from '@/components/UI/modal_areyousure.vue'
import { ArrowRight, Circle, Redo, Undo } from 'lucide-vue-next'
import { nextTick, onMounted, ref, watch } from 'vue'

interface Props {
  imageUrl: string
}

interface Point {
  x: number
  y: number
}

interface Drawing {
  type: 'circle' | 'arrow'
  points: Point[]
  color: string
  width: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  save: [dataUrl: string]
  cancel: []
}>()

const canvasRef = ref<HTMLCanvasElement | null>(null)
const containerRef = ref<HTMLDivElement | null>(null)
const currentTool = ref<'circle' | 'arrow'>('circle')
const strokeColor = ref('#ff0000')
const strokeWidth = ref(5)

const isDrawing = ref(false)
const startPoint = ref<Point | null>(null)
const currentPoint = ref<Point | null>(null)

const drawings = ref<Drawing[]>([])
const history = ref<Drawing[][]>([])
const historyIndex = ref(-1)

const canUndo = ref(false)
const canRedo = ref(false)

let image: HTMLImageElement | null = null

function setTool(tool: 'circle' | 'arrow') {
  currentTool.value = tool
}

function getMousePos(e: MouseEvent): Point {
  if (!canvasRef.value) return { x: 0, y: 0 }
  const rect = canvasRef.value.getBoundingClientRect()
  return {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top,
  }
}

function startDrawing(e: MouseEvent) {
  if (!canvasRef.value) return
  isDrawing.value = true
  startPoint.value = getMousePos(e)
  currentPoint.value = startPoint.value
}

function draw(e: MouseEvent) {
  if (!isDrawing.value || !startPoint.value || !canvasRef.value) return
  currentPoint.value = getMousePos(e)
  redraw()
}

function stopDrawing() {
  if (!isDrawing.value || !startPoint.value || !currentPoint.value) return

  // Ajouter le dessin à l'historique
  const newDrawing: Drawing = {
    type: currentTool.value,
    points: [startPoint.value, currentPoint.value],
    color: strokeColor.value,
    width: strokeWidth.value,
  }

  saveToHistory()
  drawings.value.push(newDrawing)
  saveToHistory()

  isDrawing.value = false
  startPoint.value = null
  currentPoint.value = null
  redraw()
}

function saveToHistory() {
  // Supprimer l'historique après l'index actuel si on fait une nouvelle action
  if (historyIndex.value < history.value.length - 1) {
    history.value = history.value.slice(0, historyIndex.value + 1)
  }
  history.value.push(JSON.parse(JSON.stringify(drawings.value)))
  historyIndex.value = history.value.length - 1
  updateUndoRedo()
}

function undo() {
  if (historyIndex.value > 0) {
    historyIndex.value--
    drawings.value = JSON.parse(JSON.stringify(history.value[historyIndex.value]))
    redraw()
    updateUndoRedo()
  }
}

function redo() {
  if (historyIndex.value < history.value.length - 1) {
    historyIndex.value++
    drawings.value = JSON.parse(JSON.stringify(history.value[historyIndex.value]))
    redraw()
    updateUndoRedo()
  }
}

function updateUndoRedo() {
  canUndo.value = historyIndex.value > 0
  canRedo.value = historyIndex.value < history.value.length - 1
}

function redraw() {
  if (!canvasRef.value || !image) return

  const ctx = canvasRef.value.getContext('2d')
  if (!ctx) return

  // Effacer le canvas
  ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height)

  // Redessiner l'image
  ctx.drawImage(image, 0, 0, canvasRef.value.width, canvasRef.value.height)

  // Redessiner tous les dessins
  drawings.value.forEach(drawing => {
    drawShape(ctx, drawing)
  })

  // Dessiner le dessin en cours
  if (isDrawing.value && startPoint.value && currentPoint.value) {
    const tempDrawing: Drawing = {
      type: currentTool.value,
      points: [startPoint.value, currentPoint.value],
      color: strokeColor.value,
      width: strokeWidth.value,
    }
    drawShape(ctx, tempDrawing)
  }
}

function drawShape(ctx: CanvasRenderingContext2D, drawing: Drawing) {
  ctx.strokeStyle = drawing.color
  ctx.fillStyle = drawing.color
  ctx.lineWidth = drawing.width
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  if (drawing.points.length < 2) return

  const [start, end] = drawing.points

  if (drawing.type === 'circle') {
    const centerX = (start.x + end.x) / 2
    const centerY = (start.y + end.y) / 2
    const radiusX = Math.abs(end.x - start.x) / 2
    const radiusY = Math.abs(end.y - start.y) / 2
    const radius = Math.max(radiusX, radiusY)

    ctx.beginPath()
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI)
    ctx.stroke()
  } else if (drawing.type === 'arrow') {
    // Dessiner la ligne
    ctx.beginPath()
    ctx.moveTo(start.x, start.y)
    ctx.lineTo(end.x, end.y)
    ctx.stroke()

    // Dessiner la pointe de la flèche
    const angle = Math.atan2(end.y - start.y, end.x - start.x)
    const arrowLength = 20
    const arrowAngle = Math.PI / 6

    ctx.beginPath()
    ctx.moveTo(end.x, end.y)
    ctx.lineTo(
      end.x - arrowLength * Math.cos(angle - arrowAngle),
      end.y - arrowLength * Math.sin(angle - arrowAngle)
    )
    ctx.lineTo(
      end.x - arrowLength * Math.cos(angle + arrowAngle),
      end.y - arrowLength * Math.sin(angle + arrowAngle)
    )
    ctx.closePath()
    ctx.fill()
  }
}

const showClearModal = ref(false)

function clear() {
  showClearModal.value = true
}

function confirmClear() {
  drawings.value = []
  saveToHistory()
  redraw()
  showClearModal.value = false
}

function cancelClear() {
  showClearModal.value = false
}

function save() {
  if (!canvasRef.value) return
  const dataUrl = canvasRef.value.toDataURL('image/png')
  emit('save', dataUrl)
}

async function loadImage() {
  if (!canvasRef.value || !containerRef.value) return

  return new Promise<void>((resolve, reject) => {
    image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => {
      if (!canvasRef.value || !containerRef.value || !image) return

      // Calculer les dimensions pour s'adapter au conteneur
      const containerWidth = containerRef.value.clientWidth
      const containerHeight = containerRef.value.clientHeight - 200 // Réserver de l'espace pour la toolbar

      const imageAspect = image.width / image.height
      const containerAspect = containerWidth / containerHeight

      let canvasWidth: number
      let canvasHeight: number

      if (imageAspect > containerAspect) {
        // Image plus large
        canvasWidth = Math.min(containerWidth, image.width)
        canvasHeight = canvasWidth / imageAspect
      } else {
        // Image plus haute
        canvasHeight = Math.min(containerHeight, image.height)
        canvasWidth = canvasHeight * imageAspect
      }

      // Limiter à 70vh max
      const maxHeight = window.innerHeight * 0.7
      if (canvasHeight > maxHeight) {
        canvasHeight = maxHeight
        canvasWidth = canvasHeight * imageAspect
      }

      canvasRef.value.width = canvasWidth
      canvasRef.value.height = canvasHeight

      const ctx = canvasRef.value.getContext('2d')
      if (ctx) {
        ctx.drawImage(image, 0, 0, canvasWidth, canvasHeight)
      }

      // Initialiser l'historique avec l'état vide
      history.value = [[]]
      historyIndex.value = 0
      updateUndoRedo()
      resolve()
    }
    image.onerror = reject
    image.src = props.imageUrl
  })
}

watch(
  () => props.imageUrl,
  () => {
    if (props.imageUrl) {
      loadImage()
    }
  },
  { immediate: true }
)

onMounted(async () => {
  await nextTick()
  if (props.imageUrl) {
    await loadImage()
  }
})
</script>

<style scoped>
.screenshot-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 80vh;
}

.toolbar {
  flex-shrink: 0;
}

.canvas-container {
  flex: 1;
  min-height: 0;
}

canvas {
  display: block;
  max-width: 100%;
  max-height: 100%;
}
</style>
